import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import PostCard from "@/components/base/PostCard";
import HotSidebar from "@/components/feature/HotSidebar";
import useSeo from "@/hooks/useSeo";
import { categories, hotTopics, posts } from "@/mocks/community";
import CategoryTabs from "./components/CategoryTabs";
import TopicRankList from "./components/TopicRankList";

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const keyword = searchParams.get("q") ?? "";

  const setCategory = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("category");
    else next.set("category", key);
    setSearchParams(next);
  };

  const clearKeyword = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next);
  };

  const filteredTopics = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return hotTopics.filter((topic) => {
      const matchCategory = activeCategory === "all" || topic.category === activeCategory;
      const matchKeyword =
        !q || `${topic.title} ${topic.categoryName}`.toLowerCase().includes(q);
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const filteredPosts = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return posts.filter((post) => {
      const matchCategory = activeCategory === "all" || post.category === activeCategory;
      const matchKeyword =
        !q || [post.title, post.excerpt, post.categoryName, ...post.tags].join(" ").toLowerCase().includes(q);
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const activeName =
    activeCategory === "all" ? "全部板块" : categories.find((c) => c.key === activeCategory)?.name ?? "全部板块";

  const jsonLd = useMemo(() => {
    const origin = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${activeName} · 板块热榜`,
        description: "按板块浏览今日活跃话题榜与高互动爆料帖，支持关键词筛选与热度排序。",
        url: `${origin}/topics`,
        isPartOf: { "@type": "WebSite", name: "爆料社区", url: origin },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: origin },
          { "@type": "ListItem", position: 2, name: "话题榜", item: `${origin}/topics` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "板块热榜",
        itemListElement: hotTopics.map((topic, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: topic.title,
          url: `${origin}/post/${topic.postId}`,
        })),
      },
    ];
  }, [activeName]);

  useSeo({
    title: `${activeName} · 板块热榜与今日活跃话题 | 爆料社区`,
    description:
      "在话题榜中按板块浏览今日活跃话题榜与高互动爆料帖，支持关键词筛选和热度排序，快速找到正在热烈讨论的新帖与热门话题。",
    keywords: "板块热榜,今日活跃话题,话题榜,高互动爆料帖,新帖热门讨论",
    path: "/topics",
    type: "website",
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-container px-4 pb-8 pt-24 md:px-6 md:pb-10 md:pt-28">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "话题榜" }]} />
          <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                {activeName} · 今日活跃话题榜
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                这里汇总了各板块正在被热议的话题与高互动爆料帖，热度排名会随着讨论持续刷新。选择板块或输入关键词，快速定位你想围观的内容。
              </p>
            </div>
            <div className="flex gap-6">
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">{filteredTopics.length}</p>
                <p className="text-xs text-foreground-500">上榜话题</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{filteredPosts.length}</p>
                <p className="text-xs text-foreground-500">相关帖子</p>
              </div>
            </div>
          </div>

          {keyword && (
            <div className="mt-5 flex flex-wrap items-center gap-2 text-sm text-foreground-700">
              <span className="text-foreground-500">当前筛选：</span>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-primary-700">
                关键词「{keyword}」
                <button type="button" onClick={clearKeyword} aria-label="清除关键词筛选" className="cursor-pointer">
                  <i className="ri-close-line" aria-hidden="true" />
                </button>
              </span>
            </div>
          )}

          <div className="mt-6">
            <CategoryTabs value={activeCategory} onChange={setCategory} />
          </div>
        </div>
      </div>

      <div className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid max-w-container gap-8 px-4 md:px-6 lg:grid-cols-[1.7fr_1fr]">
          <div>
            <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-bar-chart-box-line text-primary-500" aria-hidden="true" />
              板块热榜
            </h2>
            <TopicRankList topics={filteredTopics} />

            <h2 className="mb-5 mt-12 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-article-line text-secondary-500" aria-hidden="true" />
              相关爆料帖
            </h2>
            {filteredPosts.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {filteredPosts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
                <p className="text-sm text-foreground-600">该筛选条件下暂时没有帖子，试试切换板块。</p>
              </div>
            )}
          </div>
          <HotSidebar />
        </div>
      </div>
    </SiteLayout>
  );
}